import React, {useEffect, useState} from 'react';
import {StyleSheet, View, Platform} from 'react-native';

import {observer} from 'mobx-react';

import {RNEText, RNEButton} from '../../components';

import {colorPalette, colors} from '../../../core/config/color.config';

import {
  useGenerateTemporaryToken,
  useGetInvoice,
} from '../../server/react-query/usePayment';

import {formatAmount} from '../../utils/currency.utils';
import {useNavigation} from '@react-navigation/native';
import {NavigationKey} from '../../navigations/config';
import {useToast} from 'react-native-fast-toast';
import {ConsultationCancelConfirmationModal, PaymentMethodSelectionModal} from '..';
import {Overlay} from 'react-native-elements'; // rne-netzon replaced
import usePayment from '../../hooks/payment/usePayment';
import { width, height, totalSize } from 'react-native-dimension';
import { useCancelConsultationDetail } from '../../server/react-query/useConsultation';
import { ConsultationStatusEnum } from '../../server/types/consultation.types';

interface ConsultationProceedToPaymentProps {
  serviceId: string;
  patientId: string;
  serviceRequestId: string
}

const ConsultationProceedToPayment = (
  props: ConsultationProceedToPaymentProps,
) => {
  const navigation = useNavigation();
  const toast = useToast();
  const [totalAmount, setTotalAmount] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [webRedirect, setWebRedirect] = useState(false);
  const [isCancelConfirmModalVisible, setIsCancelConfirmModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {data} = useGetInvoice({
    serviceId: props.serviceId,
    patientId: props.patientId,
  });

  const {prepareIPay88Values, setSelectedPaymentMethod} = usePayment();

  const mutation = useGenerateTemporaryToken();

  const cancelMutation = useCancelConsultationDetail({
    serviceRequestId: props.serviceRequestId,
    status: ConsultationStatusEnum.CANCELLED,
  });

  const proceedToPayment = async () => {
    try {
      const response = await mutation.mutateAsync({
        patientId: props.patientId,
        invoiceId: data?.[0]?.invoiceId || '',
      });

      // console.log('TOKEN', response.token)
    
      navigation.navigate(NavigationKey.CONSULTATION_PAYMENT_WEB_VIEW, {
        token: response.token,
      });
    } catch (error) {
      console.log('error', error);
      if (error?.data?.error) {
        toast?.show(error?.data?.error, {type: 'danger'});
      }
    }
  };

  const getSelectedPaymentMethod = (code: string) => {
    setSelectedPaymentMethod(code);
  };

  useEffect(() => {
    const amount = data?.[0]?.totalGross?.value || 0;
    setTotalAmount(amount);
  }, [data]);

  const handleDeleteRequest = async () => {
    try {
      setIsLoading(true);
      await cancelMutation.mutateAsync();

      setTimeout(()=>{
        setIsCancelConfirmModalVisible(!isCancelConfirmModalVisible)
        setIsLoading(false);
      },1000);
      
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Overlay
        isVisible={showDialog}
        onBackdropPress={() => setShowDialog(false)}
        overlayStyle={webStyles.overlayContainer}>
        <PaymentMethodSelectionModal
          onConfirm={() => {
            setShowDialog(false);
            setWebRedirect(true);
            prepareIPay88Values(data?.[0]!, true);
          }}
          onSelectPaymentMethod={getSelectedPaymentMethod}
        />
      </Overlay>
      {Platform.OS === 'web' ? (
        <View style={webStyles.mainContainer}>
          <View style={webStyles.messageContainer}>
            <RNEText
              label="Your doctor has accepted your request."
              weight="bold"
              color="accent"
              variant="button"
              numberOfLines={3}
              ellipsizeMode="tail"
            />
            <RNEText
              label={`Total due is P${formatAmount(
                totalAmount,
              )}. Proceed with payment to start the consultation.`}
              color="blackLight"
              variant="button"
              numberOfLines={3}
              ellipsizeMode="tail"
            />
          </View>

          <View>
            <RNEButton
              title="Proceed to Payment"
              color="primary"
              type="outline"
              variant="outlined"
              style={webStyles.buttonStyle}
              onPress={() => setShowDialog(true)}
              loading={webRedirect}
            />
          </View>
        </View>
      ) : (
        <>
          <View style={mobileStyles.mainContainer}>
            <View style={mobileStyles.messageContainer}>
              <RNEText
                label="Your doctor has accepted your request."
                weight="bold"
                color="accent"
                numberOfLines={3}
                ellipsizeMode="tail"
                align="center"
              />
              <RNEText
                label={`Total due is P${formatAmount(
                  totalAmount,
                )}. Proceed with payment to start the consultation.`}
                color="grey"
                numberOfLines={3}
                ellipsizeMode="tail"
                align="center"
              />
            </View>

            <View style={mobileStyles.footerBtnContainer}>

              <View>
                <RNEButton
                  title="Cancel Request"
                  color="primary"
                  type="outline"
                  variant="outlined"
                  style={mobileStyles.buttonStyle}
                  onPress={() =>
                    setIsCancelConfirmModalVisible(!isCancelConfirmModalVisible)
                  }
                  titleStyle={{fontSize: totalSize(1.5)}}
                />
              </View>

              <View>
                <RNEButton
                  title="Proceed to Payment"
                  color={colors.brightYellow.regular}
                  type="solid"
                  variant="default"
                  style={mobileStyles.buttonStyle}
                  onPress={proceedToPayment}
                  titleStyle={{fontSize: totalSize(1.5)}}
                />
              </View>
              
            </View>
          </View>

          <Overlay
            isVisible={isCancelConfirmModalVisible}
            overlayStyle={sharedStyles.overlayContainer}
          >
              <ConsultationCancelConfirmationModal
                isLoading={isLoading}
                title={"Are you sure you want to cancel this request?"}
                primaryBtnText={"Yes"}
                secondaryBtnText={"No"}
                onConfirm={handleDeleteRequest}
                onCancel={() =>
                  setIsCancelConfirmModalVisible(!isCancelConfirmModalVisible)
                }
              />
          </Overlay>
        </>
      )}
    </>
  );
};

export default observer(ConsultationProceedToPayment);

const webStyles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: colorPalette.light,
  },
  messageContainer: {
    flex: 1,
    justifyContent: 'center',
    marginRight: 16,
  },
  buttonStyle: {
    width: 180,
  },
  overlayContainer: {
    borderRadius: 20,
    marginHorizontal: 40,
  },
});

const mobileStyles = StyleSheet.create({
  mainContainer: {
    alignItems: 'center',
    paddingVertical: height(3.5),
    borderTopWidth: width(0.2),
    borderTopColor: colorPalette.light,
  },
  messageContainer: {
    alignItems: 'center',
    marginBottom: height(3.5),
  },
  buttonStyle: {
    width: Platform.OS === 'android' ? width(45) : width(45),
  },
  footerBtnContainer:{
    flexDirection: 'row', 
    gap: width(4)
  }
});

const sharedStyles = StyleSheet.create({
  overlayContainer: {
    borderRadius: totalSize(2.5),
    marginHorizontal: 40,
    width: width(90),
    borderColor: 'black',
    borderWidth: totalSize(0.15),
  },
});